// src/screens/Dashboard.tsx
import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { LogoLoader } from "../components/LogoLoader";
import {
  Hand,
  BarChart2,
  CalendarDays,
  Newspaper,
  CalendarClock,
  User,
  Users as UsersIcon,
} from "lucide-react";
import i18n from "../i18n";

// --- Hook i18n "safe" (évite TS2589) ---
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

// --- Types ---
type PlayerStat = {
  player_name: string;
  player_number: number;
  position: string;
  points: number;
  assists: number;
  rebounds: number;
  steals: number;
  blocks: number;
  turnovers: number;
  is_opponent?: boolean | null;
};

type NewsItem = {
  id: string;
  title: string;
  content: string;
  created_at: string;
};

// Type qui correspond à ta table `news` en base (avec FR/EN)
type NewsRow = {
  id: string;
  created_at: string;
  title_fr: string | null;
  title_en: string | null;
  content_fr: string | null;
  content_en: string | null;
};

type NextEvent = {
  type: "match" | "training";
  id: string;
  date: string;
  label: string;
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const { t, lng } = useI18nSafe();

  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Dernier match + stats
  const [lastMatchId, setLastMatchId] = useState<string | null>(null);
  const [lastMatchOpponent, setLastMatchOpponent] = useState<string | null>(null);
  const [lastMatchDate, setLastMatchDate] = useState<string | null>(null);
  const [lastMatchStats, setLastMatchStats] = useState<PlayerStat[]>([]);

  // News + prochain évènement
  const [latestNews, setLatestNews] = useState<NewsItem | null>(null);
  const [nextEvent, setNextEvent] = useState<NextEvent | null>(null);

  const locale = lng.startsWith("fr") ? "fr-FR" : "en-US";

  useEffect(() => {
    const init = async () => {
      const { data: auth } = await supabase.auth.getUser();
      const currentUser = auth?.user;
      if (!currentUser) {
        navigate("/login");
        return;
      }
      setUser(currentUser);

      // ============== Récupérer les équipes accessibles (owner + team_members actifs) ==============
      const [{ data: ownTeams }, { data: memberRows }] = await Promise.all([
        supabase.from("teams").select("id").eq("user_id", currentUser.id).or("archived.is.null,archived.eq.false"),
        supabase
          .from("team_members")
          .select("team_id, status")
          .eq("user_id", currentUser.id)
          .eq("status", "active"),
      ]);

      const allTeamIds = [
        ...(ownTeams?.map((t: any) => t.id) ?? []),
        ...(memberRows?.map((m: any) => m.team_id) ?? []),
      ].filter(Boolean) as string[];

      // Exclure les équipes archivées côté member
      const { data: activeTeams } = allTeamIds.length > 0
        ? await supabase.from("teams").select("id").in("id", allTeamIds).or("archived.is.null,archived.eq.false")
        : { data: [] };
      const teamIds = (activeTeams?.map((t: any) => t.id) ?? []) as string[];

      // ============== Dernier match avec stats (sur les équipes accessibles) ==============
      if (teamIds.length > 0) {
        // On prend les 10 derniers matchs et on trouve le plus récent avec des stats POUR MON ÉQUIPE
        const { data: recentMatches } = await supabase
          .from("matches")
          .select("id, opponent, date, team_id")
          .in("team_id", teamIds)
          .order("date", { ascending: false })
          .limit(10);

        if (recentMatches?.length) {
          for (const m of recentMatches as { id: string; opponent: string; date: string }[]) {
            const { data: stats } = await supabase
              .from("match_stats")
              .select(
                "player_name, player_number, position, points, assists, rebounds, steals, blocks, turnovers, is_opponent"
              )
              .eq("match_id", m.id);

            if (stats && stats.length > 0) {
              // ❗ On garde seulement les stats de MON équipe (pas is_opponent)
              const teamStats = (stats as PlayerStat[]).filter((s) => !s.is_opponent);

              // si ce match a des stats pour mon équipe, c'est celui qu'on affiche
              if (teamStats.length > 0) {
                setLastMatchId(m.id);
                setLastMatchOpponent(m.opponent);
                setLastMatchDate(m.date);
                setLastMatchStats(teamStats); // 👈 uniquement mon équipe
                break;
              }
            }
          }
        }
      }

      // ============== Dernière news (globale) avec gestion de la langue ==============
      const isFr = lng.startsWith("fr");

      const { data: rawNews } = await supabase
        .from("news")
        .select("id, created_at, title_fr, title_en, content_fr, content_en")
        .order("created_at", { ascending: false })
        .limit(1);

      if (rawNews && rawNews.length > 0) {
        const row = rawNews[0] as NewsRow;

        const title = isFr
          ? row.title_fr ?? row.title_en ?? ""
          : row.title_en ?? row.title_fr ?? "";

        const content = isFr
          ? row.content_fr ?? row.content_en ?? ""
          : row.content_en ?? row.content_fr ?? "";

        setLatestNews({
          id: row.id,
          created_at: row.created_at,
          title,
          content,
        });
      } else {
        setLatestNews(null);
      }

      // ============== Prochain événement (match OU entraînement) pour ces équipes ==============
      if (teamIds.length > 0) {
        const todayISO = new Date().toISOString().slice(0, 10);

        const [{ data: nextMatches }, { data: nextTrainings }] = await Promise.all([
          supabase
            .from("matches")
            .select("id, opponent, date, team_id")
            .in("team_id", teamIds)
            .gte("date", todayISO)
            .order("date", { ascending: true })
            .limit(1),
          supabase
            .from("trainings")
            .select("id, date, team_id")
            .in("team_id", teamIds)
            .gte("date", todayISO)
            .order("date", { ascending: true })
            .limit(1),
        ]);

        let nextTrainingLabel: string | null = null;
        if (nextTrainings && nextTrainings.length > 0) {
          const tr = nextTrainings[0] as { id: string; date: string; team_id?: string | null };
          if (tr.team_id) {
            const { data: teamRow } = await supabase
              .from("teams")
              .select("team_name")
              .eq("id", tr.team_id)
              .maybeSingle();
            nextTrainingLabel = teamRow?.team_name
              ? t("dashboard:training_with_team", { team: teamRow.team_name })
              : t("dashboard:training");
          } else {
            nextTrainingLabel = t("dashboard:training");
          }
        }

        const candidates: NextEvent[] = [];
        if (nextMatches?.length) {
          const nm = nextMatches[0] as { id: string; opponent: string; date: string };
          candidates.push({
            type: "match",
            id: nm.id,
            date: nm.date,
            label: t("dashboard:match_vs", { opponent: nm.opponent }),
          });
        }
        if (nextTrainings?.length) {
          const nt = nextTrainings[0] as { id: string; date: string };
          candidates.push({
            type: "training",
            id: nt.id,
            date: nt.date,
            label: nextTrainingLabel || t("dashboard:training"),
          });
        }

        if (candidates.length > 0) {
          candidates.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
          setNextEvent(candidates[0]);
        } else {
          setNextEvent(null);
        }
      } else {
        setNextEvent(null);
      }

      setLoading(false);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, lng]); // 👈 re-run quand la langue change

  if (loading) {
    // 🔥 nouveau loader avec ton GIF
    return <LogoLoader />;
  }

  const formatDateTime = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleString(locale, {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

  const relativeIn = (iso: string) => {
    const now = Date.now();
    const tms = new Date(iso).getTime();
    const diffDays = Math.ceil((tms - now) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return t("dashboard:today");
    if (diffDays === 1) return t("dashboard:in_one_day");
    return t("dashboard:in_n_days", { count: diffDays });
  };

  const getEventHref = (e: NextEvent) => (e.type === "match" ? `/matchdetails/${e.id}` : `/training/${e.id}`);

  return (
    <section className="min-h-screen flex flex-col items-center p-4 sm:p-6 bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-orange-500 dark:text-orange-400">
        {t("dashboard:title")}
      </h1>

      {user ? (
        <div className="text-center flex flex-col items-center space-y-4 w-full">
          <p className="text-gray-700 dark:text-gray-300 flex items-center justify-center gap-2 text-sm sm:text-base">
            {t("dashboard:welcome")},{" "}
            <span className="font-semibold">{user.email}</span>
            <Hand className="w-5 h-5 text-orange-500 dark:text-orange-400" />
          </p>

          <p className="mb-4 sm:mb-6 text-gray-700 dark:text-gray-300 flex flex-col sm:flex-row items-center gap-2 text-sm sm:text-base">
            <span>{t("dashboard:subtitle_intro")}</span>
            <span className="inline-flex items-center gap-1 text-gray-800 dark:text-gray-200">
              <BarChart2 className="w-4 h-4" />
            </span>
            <span>{t("dashboard:subtitle_cta")}</span>
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full max-w-2xl">
            <Link
              to="/profile"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#fc7825] hover:bg-orange-600 text-white font-semibold py-3 px-4 rounded-lg transition
                        focus:outline-none focus:ring-2 focus:ring-orange-400/60"
            >
              <User className="w-5 h-5" />
              {t("dashboard:my_profile")}
            </Link>

            <Link
              to="/myteam"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition
                        focus:outline-none focus:ring-2 focus:ring-green-400/60 dark:bg-green-600 dark:hover:bg-green-700"
            >
              <UsersIcon className="w-5 h-5" />
              {t("dashboard:view_my_teams")}
            </Link>
          </div>

          {/* Prochain événement */}
          {nextEvent && (
            <Link
              to={getEventHref(nextEvent)}
              className="mt-6 sm:mt-10 w-full max-w-2xl bg-white border border-gray-200 p-4 sm:p-5 rounded-2xl shadow-sm
                         dark:bg-gray-900 dark:border-gray-800 block group focus:outline-none focus:ring-2 focus:ring-purple-400/60
                         hover:shadow-md transition"
              aria-label={
                nextEvent.type === "match"
                  ? t("dashboard:open_match_of_date", { date: formatDateTime(nextEvent.date) })
                  : t("dashboard:open_training_of_date", { date: formatDateTime(nextEvent.date) })
              }
            >
              <h2 className="text-lg sm:text-xl font-bold mb-2 text-purple-600 dark:text-purple-400 flex items-center gap-2">
                <CalendarClock className="w-5 h-5" />
                {t("dashboard:next_event")}
                <span className="ml-auto text-xs text-purple-600/70 dark:text-purple-300/70 opacity-0 group-hover:opacity-100 transition">
                  {t("dashboard:open_arrow")}
                </span>
              </h2>
              <div className="text-sm text-gray-700 dark:text-gray-300 text-left">
                <div className="font-semibold">{nextEvent.label}</div>
                <div className="text-gray-600 dark:text-gray-400">
                  {formatDateTime(nextEvent.date)} — <span className="italic">{relativeIn(nextEvent.date)}</span>
                </div>
              </div>
            </Link>
          )}

          {/* Dernier match */}
          {lastMatchId && (
            <div className="mt-6 sm:mt-10 w-full max-w-2xl bg-white border border-gray-200 p-4 sm:p-5 rounded-2xl shadow-sm
                            dark:bg-gray-900 dark:border-gray-800">
              <h2 className="text-lg sm:text-xl font-bold mb-2 text-orange-500 dark:text-orange-400 flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                {t("dashboard:last_recorded_match")}
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
                {t("dashboard:vs_opponent_on", {
                  opponent: lastMatchOpponent,
                  date: new Date(lastMatchDate!).toLocaleDateString(locale),
                })}
              </p>

              {/* MOBILE: cartes */}
              <ul className="sm:hidden space-y-3">
                {lastMatchStats.map((s, idx) => (
                  <li
                    key={idx}
                    className="rounded-xl border border-gray-200 dark:border-gray-800 p-3 bg-white/60 dark:bg-gray-900/60"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full bg-gray-100 dark:bg-gray-800">
                            {s.player_number}
                          </span>
                          <p className="font-semibold text-sm truncate">{s.player_name}</p>
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                            {s.position}
                          </span>
                        </div>
                        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                          {t("dashboard:individual_performance")}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-xl font-extrabold leading-none">{s.points}</span>
                        <div className="text-[10px] text-gray-500 dark:text-gray-400">{t("dashboard:stats.pts")}</div>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-5 gap-1.5 text-[11px]">
                      <div className="flex flex-col items-center rounded-md border border-gray-200 dark:border-gray-800 py-1">
                        <span className="font-semibold">{s.assists}</span>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">{t("dashboard:stats.ast")}</span>
                      </div>
                      <div className="flex flex-col items-center rounded-md border border-gray-200 dark:border-gray-800 py-1">
                        <span className="font-semibold">{s.rebounds}</span>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">{t("dashboard:stats.reb")}</span>
                      </div>
                      <div className="flex flex-col items-center rounded-md border border-gray-200 dark:border-gray-800 py-1">
                        <span className="font-semibold">{s.steals}</span>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">{t("dashboard:stats.stl")}</span>
                      </div>
                      <div className="flex flex-col items-center rounded-md border border-gray-200 dark:border-gray-800 py-1">
                        <span className="font-semibold">{s.blocks}</span>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">{t("dashboard:stats.blk")}</span>
                      </div>
                      <div className="flex flex-col items-center rounded-md border border-gray-200 dark:border-gray-800 py-1">
                        <span className="font-semibold">{s.turnovers}</span>
                        <span className="text-[10px] text-gray-500 dark:text-gray-400">{t("dashboard:stats.to")}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              {/* DESKTOP: tableau */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm text-left min-w-[720px] border-t border-gray-200 dark:border-gray-800">
                  <thead className="bg-gray-100 dark:bg-gray-800">
                    <tr className="text-gray-700 dark:text-gray-200">
                      <th className="p-2">{t("dashboard:table.num")}</th>
                      <th className="p-2">{t("dashboard:table.name")}</th>
                      <th className="p-2">{t("dashboard:table.pos")}</th>
                      <th className="p-2">{t("dashboard:stats.pts")}</th>
                      <th className="p-2">{t("dashboard:stats.ast")}</th>
                      <th className="p-2">{t("dashboard:stats.reb")}</th>
                      <th className="p-2">{t("dashboard:stats.stl")}</th>
                      <th className="p-2">{t("dashboard:stats.blk")}</th>
                      <th className="p-2">{t("dashboard:stats.to")}</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-800 dark:text-gray-200">
                    {lastMatchStats.map((stat, idx) => (
                      <tr
                        key={idx}
                        className="border-b border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/60"
                      >
                        <td className="p-2">{stat.player_number}</td>
                        <td className="p-2">{stat.player_name}</td>
                        <td className="p-2">{stat.position}</td>
                        <td className="p-2">{stat.points}</td>
                        <td className="p-2">{stat.assists}</td>
                        <td className="p-2">{stat.rebounds}</td>
                        <td className="p-2">{stat.steals}</td>
                        <td className="p-2">{stat.blocks}</td>
                        <td className="p-2">{stat.turnovers}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {latestNews && (
            <div className="mt-6 sm:mt-10 w-full max-w-2xl bg-white border border-gray-200 p-4 sm:p-5 rounded-2xl shadow-sm
                            dark:bg-gray-900 dark:border-gray-800 text-left">
              <h2 className="text-lg sm:text-xl font-bold mb-2 text-blue-600 dark:text-blue-400 flex items-center gap-2">
                <Newspaper className="w-5 h-5" />
                {t("dashboard:latest_news")}
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1">
                {new Date(latestNews.created_at).toLocaleDateString(locale)}
              </p>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {latestNews.title}
              </h3>
              <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
                {latestNews.content}
              </p>
            </div>
          )}
        </div>
      ) : (
        <p className="text-gray-500 dark:text-gray-400">{t("common:not_logged_in")}</p>
      )}
    </section>
  );
};
